import { JSX, useEffect, useState } from 'react';

import { mapCurrencyToSymbol } from '../../utils/currency.util';
import Button from '../button/button';
import CreditCardForm from '../credit-card-form/credit-card-form';
import { PaymentFormData } from '../../types/payment-form-data.type';
import { CreditCard } from '../../types/credit-card.type';
import { Bank, PaymentMethod } from '../../types/payment-config.type';
import CouponCode from '../coupon-code/coupon-code';
import { paymentsSDK } from '../../webservices/payment.webservice';
import ResponseCodes from '../../constants/response-codes.constant';
import { validateSchema, ValidationErrors } from '../../utils/validate-schema';
import { CasheaForm } from '../method-forms/cashea-form/cashea-form';
import { ZelleForm } from '../method-forms/zelle-form/zelle-form';
import { MobilePaymentForm } from '../method-forms/mobile-payment-form/mobile-payment-form';

import styles from './payment-form.module.scss';
import { casheaSchema, mobilePaymentSchema, zelleSchema } from './form-schemas';

interface Props {
    currency: string;
    rate: number;
    amount: number;
    method: PaymentMethod;
    type: string;
    onPaymentData: (payment: PaymentFormData) => void;
    onDiscount?: (discount: number) => void;
    banks: Bank[];
}

type MobilePaymentData = {
    bankId: number;
    date: string;
    phone: string;
    nin: string;
    referenceCode: string;
};

const PaymentForm = (props: Props) => {
    const [buttonProps, setButtonProps] = useState({
        disabled: true,
        text: 'Confirmar',
    });

    const [zellePayment, setZellePayment] = useState('');
    const [casheaPayment, setCasheaPayment] = useState('');
    const [mobilePayment, setMobilePayment] = useState<MobilePaymentData>();
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        new Map()
    );

    const [creditCard, setCreditCard] = useState<CreditCard>({
        number: '',
        expMonth: '',
        expYear: '',
        cvc: '',
    });
    const [coupon, setCoupon] = useState<{
        code?: string;
        disabled: boolean;
        afterApply?: {
            applied: boolean;
            errorMessage: string;
            discount?: number;
        };
    }>({
        code: undefined,
        disabled: false,
        afterApply: undefined,
    });

    useEffect(() => {
        validatePayment(props.method);
    }, [zellePayment, casheaPayment, mobilePayment, creditCard]);

    const validatePayment = (method: PaymentMethod) => {
        if (!method) return;
        switch (method.code) {
            case 'TDCSTRIPE':
                return setValidCreditCard(true);
            case 'TZELLE':
                return validateZelle();
            case 'CASHEA':
                return validateCashea();
            case 'PMBVC':
            default:
                return validateMobilePayment();
        }
    };

    const validateZelle = async () => {
        const errors = validateSchema(zellePayment, zelleSchema);
        setValidationErrors(errors);
        setButtonProps({ text: 'Confirmar', disabled: errors.size > 0 });
    };

    const validateCashea = () => {
        const errors = validateSchema(casheaPayment, casheaSchema);
        setValidationErrors(errors);
        setButtonProps({ text: 'Confirmar', disabled: errors.size > 0 });
    };

    const validateMobilePayment = async () => {
        const errors = validateSchema(mobilePayment, mobilePaymentSchema);
        setValidationErrors(errors);
        setButtonProps({ text: 'Confirmar', disabled: errors.size > 0 });
    };

    const setValidCreditCard = (valid: boolean) => {
        setButtonProps({ text: 'Confirmar', disabled: !valid });
    };

    const sendPaymentRequest = () => {
        setButtonProps({ text: 'Cargando...', disabled: true });
        const paymentData: PaymentFormData = {
            amount: props.amount.withRate(props.rate),
            currency: props.currency,
        };

        switch (props.method.code) {
            case 'TDCSTRIPE':
                paymentData.card = creditCard;
                break;
            case 'TZELLE':
                paymentData.referenceCode = zellePayment;
                break;
            case 'CASHEA':
                paymentData.referenceCode = casheaPayment;
                break;
            case 'PMBVC':
                if (!mobilePayment) return;
                paymentData.bankId = mobilePayment.bankId;
                paymentData.referenceCode = mobilePayment.referenceCode;
                paymentData.claimedAt = mobilePayment.date;
                paymentData.payer = {
                    isThirdParty: false,
                    phoneNumber: `58${mobilePayment.phone.substring(1)}`,
                    document: mobilePayment.nin,
                };
                break;
            default:
        }

        paymentData.methodId = props.method.id;
        paymentData.couponCode = coupon.code;
        props.onPaymentData(paymentData);
    };

    const renderMethodForm = (method: PaymentMethod): JSX.Element | null => {
        if (!method) return null;
        switch (method.code) {
            case 'TDCSTRIPE':
                return renderCreditCard();
            case 'TZELLE':
                return renderZelle();
            case 'CASHEA':
                return renderCashea();
            case 'PMBVC':
                return renderMobilePayment();
            default:
                return <></>;
        }
    };

    const renderCashea = () => (
        <CasheaForm
            onInput={setCasheaPayment}
            error={validationErrors.get('')}
        />
    );

    const renderZelle = () => (
        <ZelleForm onInput={setZellePayment} error={validationErrors.get('')} />
    );

    const renderMobilePayment = () => (
        <MobilePaymentForm
            banks={props.banks}
            onData={setMobilePayment}
            errors={validationErrors}
        />
    );

    const renderCreditCard = (): JSX.Element => {
        return (
            <div className={styles.paymentForm}>
                <div className={styles.info}>
                    <span>Introduce tu tarjeta de crédito:</span>
                </div>
                <CreditCardForm
                    onError={() => setValidCreditCard(false)}
                    onValid={setCreditCard}
                />
            </div>
        );
    };

    const checkCouponCode = async (code: string) => {
        setCoupon((prev) => ({
            ...prev,
            disabled: true,
            afterApply: { applied: false, errorMessage: '' },
        }));
        const response = await paymentsSDK.checkCouponCode(code.trim());
        setCoupon((prev) => ({ ...prev, disabled: false }));
        if (response.code !== ResponseCodes.PROCESS_OK) {
            return setCoupon({
                code: undefined,
                disabled: false,
                afterApply: { applied: false, errorMessage: 'Coupón inválido' },
            });
        }
        const coupon = response.data;
        if (coupon.quantity < 1 || coupon.hasExpired) {
            return setCoupon({
                code: undefined,
                disabled: false,
                afterApply: { applied: false, errorMessage: 'Coupón inválido' },
            });
        }
        setCoupon({
            code,
            disabled: false,
            afterApply: {
                applied: true,
                errorMessage: '',
                discount: coupon.discount,
            },
        });
        props.onDiscount?.(coupon.discount);
    };

    const clearCoupon = () => {
        setCoupon({
            code: undefined,
            disabled: false,
            afterApply: undefined,
        });
        props.onDiscount?.(0);
    };

    return (
        <div className={styles.container}>
            {renderMethodForm(props.method)}
            <CouponCode
                onApply={checkCouponCode}
                disabled={coupon.disabled}
                afterApply={coupon.afterApply}
                clearCoupon={clearCoupon}
            />
            <footer>
                <div className={'column'}>
                    {props.type === 'recurring_licensed' && (
                        <b className={styles.recurringExplanatory}>
                            Se te cobrarán {mapCurrencyToSymbol(props.currency)}
                            {props.amount.toFixed(2)} el primer día de cada mes.
                        </b>
                    )}
                    <span>Al pagar estás aceptando nuestros</span>
                    <a
                        href={process.env.REACT_APP_TERMS_AND_CONDITIONS_LINK}
                        target={'_blank'}
                    >
                        Términos y Condiciones
                    </a>
                </div>
                <Button
                    text={buttonProps.text}
                    disabled={buttonProps.disabled}
                    onClick={sendPaymentRequest}
                />
            </footer>
        </div>
    );
};

export default PaymentForm;
